import {Lang} from 'yougile-lang'
import {logger, Util} from 'yougile-util'
import {
  CommitReq,
  dataTransmitVersion,
  minorDataTransmissionVersion,
  SandboxData
} from 'yougile-model'
import {Sync, SyncAutoSaver} from '@client/sync'
import {Backend} from '@common/backend'

/**
 * using Date instead Time here,
 * to prevent possible jumps in time
 */
const getTime = (): number => Date.now()

const healthcheckTimeout = 60_000
const syncInterval = 20_000

const timeoutStep = 30_000

const sendMinTimeout = 30_000
const sendMaxTimeout = 210_000

const receiveMinTimeout = 60_000
const receiveMaxTimeout = 320_000

export class SyncFallback {
  static latestWsResponseTime = getTime()

  static latestFallbackSendTime = getTime()
  static sendFailureCount = 0
  static sendFailureTime = 0

  static latestFallbackReceiveTime = getTime()
  static receiveFailureCount = 0
  static receiveFailureTime = 0

  static isWsHealthy(): boolean {
    const now = getTime()
    const timeIsOk = now - this.latestWsResponseTime < healthcheckTimeout
    const oldUnapprovedBranches = [...Sync.unapprovedChanges.values()].filter(
      b => !b.approved && b.attempts >= 8
    )
    return timeIsOk && oldUnapprovedBranches.length === 0
  }

  static trackResponse(): void {
    this.latestWsResponseTime = getTime()
  }

  private static initialized = false
  static init(): void {
    if (this.initialized) {
      return
    }
    this.initialized = true
    setInterval(() => {
      if (!Backend.features.syncFallback) {
        return
      }
      try {
        this.sendRoutine()
      } catch (e) {
        logger.error('Error in SyncFallback routine', e)
      }
      try {
        this.receiveRoutine()
      } catch (e) {
        logger.error('Error in SyncFallback routine', e)
      }
    }, syncInterval)
  }

  private static sendRoutine(): void {
    if (this.isWsHealthy()) {
      return
    }
    const now = getTime()
    const timeout = Math.min(
      sendMinTimeout + this.sendFailureCount * timeoutStep,
      sendMaxTimeout
    )
    if (now - this.latestFallbackSendTime > timeout) {
      void this.sendUnapprovedChanged()
    }
  }

  private static receiveRoutine(): void {
    if (this.isWsHealthy()) {
      return
    }
    const now = getTime()
    const timeout = Math.min(
      receiveMinTimeout + this.receiveFailureCount * timeoutStep,
      receiveMaxTimeout
    )
    if (now - this.latestFallbackReceiveTime > timeout) {
      void this.receiveUpdates()
    }
  }

  private static async sendUnapprovedChanged(): Promise<void> {
    this.latestFallbackSendTime = getTime()
    const branches = [...Sync.unapprovedChanges.values()]
    if (branches.length > 0) {
      logger.log('Sending branches by fallback transport', branches.length)
    }

    for (const branch of branches) {
      if (!branch || branch.approved) {
        continue
      }
      const data: Omit<CommitReq, 'branches'> = {
        minorVer: minorDataTransmissionVersion,
        version: dataTransmitVersion,
        appVersion: Backend.appVersion,
        clientType: Util.isDesktop() ? 'desktop' : 'web',
        lang: Lang.getLang(),
        branch
      }

      const resp = await Backend.postJSON<{allSucceeded: boolean}>(
        '/data/sync/send',
        {data, ...Backend.loadCred()}
      ).catch(() => ({allSucceeded: false}))

      if (resp.allSucceeded) {
        branch.approved = true
        this.sendFailureCount = 0
      } else {
        this.sendFailureCount++
        this.sendFailureTime = getTime()
      }
    }
    this.latestFallbackSendTime = getTime()
  }

  private static async receiveUpdates(): Promise<void> {
    // todo: add profile updates

    this.latestFallbackReceiveTime = getTime()

    const box = Sync.getCurrentSandbox()
    if (!box) {
      return
    }

    const resp = await Backend.postJSON<{data: SandboxData | undefined}>(
      '/data/sync/receive',
      {
        companyId: box.companyId,
        revision: box.revision,
        minorVersion: minorDataTransmissionVersion,
        ...Backend.loadCred()
      }
    ).catch(() => ({data: undefined}) as {data: SandboxData | undefined})

    if (!resp.data) {
      this.receiveFailureCount++
      this.receiveFailureTime = getTime()
    } else {
      this.receiveFailureCount = 0
      const sandboxData = resp.data
      Sync.processSandboxData(sandboxData)
      SyncAutoSaver.update()
    }

    this.latestFallbackReceiveTime = getTime()
  }
}
